/**
 * Parse warehouse location codes (frontend mirror of backend parser).
 */

const VALID_BIN_ROWS = ['Z', 'Y', 'X', 'W', 'V', 'U', 'T', 'S'];
/** Missing slot padding, bay padding, hyphen before slot, or digit before shelf letter (e.g. V-01-B-4, S-01-Q1, Z-04-0E) */
const MALFORMED_LOCATION_REGEX = /([A-Z]-\d{2}-[A-Z]-\d$)|([A-Z]-\d-[A-Z]-\d{2})|([A-Z]-\d{2}-[A-Z]\d{1,2}$)|([A-Z]-\d{2}-\d+[A-Z])/i;

/** Product spans multiple slots on one shelf, e.g. U-03-J-05+08 */
const MULTI_SLOT_BIN_REGEX = /^([ZYXWVUTS])-(\d{2})-([A-Z])-((?:\d{1,2})(?:\+\d{1,2})+)$/;

function parseMultiSlotBinSegment(upper) {
  const match = String(upper || '').match(MULTI_SLOT_BIN_REGEX);
  if (!match) return null;

  const row = match[1];
  const bay = parseInt(match[2], 10);
  const side = match[3];
  const slotNumbers = [];
  match[4].split('+').forEach((part) => {
    const num = parseInt(part, 10);
    if (!Number.isFinite(num) || num < 1) return;
    if (!slotNumbers.includes(num)) slotNumbers.push(num);
  });

  if (slotNumbers.length < 2) return null;

  const paddedSlots = slotNumbers.map((n) => String(n).padStart(2, '0'));
  const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${paddedSlots.join('+')}`;

  return { row, bay, side, slots: slotNumbers, normalized };
}

export function isMultiSlotBinLocation(code) {
  return parseMultiSlotBinSegment(normalizeLocationCode(code)) != null;
}

export function suggestLocationFix(code) {
  const upper = normalizeLocationCode(code);
  if (!upper) return null;

  let match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])-(\d)$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}-${String(parseInt(match[4], 10)).padStart(2, '0')}`;
  }

  match = upper.match(/^([ZYXWVUTS])-(\d)-([A-Z])-(\d{2})$/);
  if (match) {
    return `${match[1]}-${String(parseInt(match[2], 10)).padStart(2, '0')}-${match[3]}-${match[4]}`;
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])(\d{1,2})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}-${String(parseInt(match[4], 10)).padStart(2, '0')}`;
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-(\d+)([A-Z])-(\d{1,2})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[4]}-${String(parseInt(match[5], 10)).padStart(2, '0')}`;
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-(\d+)([A-Z])$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[4]}`;
  }

  return null;
}

export function normalizeLocationCode(code) {
  let value = code;
  // Some product docs carry metafield-shaped objects ({ value: 'P12' }) — unwrap
  // instead of letting String() coerce them to "[OBJECT OBJECT]".
  if (value && typeof value === 'object') {
    value = value.value ?? value.location ?? value.raw ?? '';
    if (value && typeof value === 'object') value = '';
  }
  const raw = String(value || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (lowered === 'not set' || lowered === 'notset' || lowered === 'null' || lowered === 'undefined') {
    return '';
  }
  return raw.toUpperCase();
}

/*
 * Quick-typing shorthand (Rhys 2026-10-01). Staff type `t5a1`, `t05a01`,
 * `t-5-a-1` or `T 5 A 1` and mean T-05-A-01: row letter, bay, shelf letter,
 * slot, with bay and slot zero-padded to two digits. One helper for every
 * location entry point so they all agree.
 */
const SHORTHAND_BIN_REGEX = /^([ZYXWVUTS])(\d{1,2})(?:([A-Z])(\d{1,2}(?:\+\d{1,2})*)?)?$/;
const SHORTHAND_LIVE_REGEX = /^([ZYXWVUTS])(?:(\d{1,2})([A-Z])(\d{0,3})|(\d{0,2}))$/;
const SHELF_ONLY_BIN_REGEX = /^([ZYXWVUTS])-(\d{2})-([A-Z])$/;

const padTwo = (digits) => String(parseInt(digits, 10)).padStart(2, '0');

/**
 * Expand a typed or scanned location to its canonical code on commit
 * (Enter, Save, scan). Bins: t5a1 → T-05-A-01, t5a → T-05-A (shelf only),
 * t5 → T-05, u3j5+8 → U-03-J-05+08. Pallets: p12 / P-12 → P12. Anything
 * else (FLOOR, END-RACK, slash composites, S-01-D-P13) comes back
 * upper-cased and otherwise untouched.
 * @param {string} input
 * @returns {string} '' for blank input
 */
export function expandLocationShorthand(input) {
  const upper = normalizeLocationCode(input);
  if (!upper) return '';
  const compact = upper.replace(/[-\s]/g, '');

  const pallet = compact.match(/^P(\d{1,3})$/);
  if (pallet) {
    const num = parseInt(pallet[1], 10);
    return num >= 1 && num <= 200 ? `P${num}` : upper;
  }

  const bin = compact.match(SHORTHAND_BIN_REGEX);
  if (!bin) return upper;
  const [, row, bay, shelf, slots] = bin;
  const head = `${row}-${padTwo(bay)}`;
  if (!shelf) return head;
  if (!slots) return `${head}-${shelf}`;
  return `${head}-${shelf}-${slots.split('+').map(padTwo).join('+')}`;
}

/**
 * As-you-type formatting for a location box: adds the hyphens and pads the
 * bay as soon as the shelf letter shows it is complete (t5a → T-05-A). Slots
 * run 01–09, so a single slot digit is padded straight away (w1d1 → W-01-D-01,
 * 2026-11-03); a second digit still types through (W-01-D-01 + 2 → W-01-D-12).
 * Call expandLocationShorthand on commit as well. Non-bin input is only
 * upper-cased, so pallets and odd codes type normally.
 * @param {string} value
 * @returns {string}
 */
export function formatLocationInputLive(value) {
  const upper = String(value || '').toUpperCase();
  const compact = upper.replace(/[-\s]/g, '');
  const match = compact.match(SHORTHAND_LIVE_REGEX);
  if (!match) return upper;
  const [, row, bay, shelf, typedSlot, bayOnly] = match;
  if (!shelf) return bayOnly ? `${row}-${bayOnly}` : row;
  let slot = typedSlot;
  if (slot.length === 3) {
    if (slot[0] !== '0') return upper;
    slot = slot.slice(1);
  } else if (/^[1-9]$/.test(slot)) {
    slot = `0${slot}`;
  }
  const formatted = [row, padTwo(bay), shelf, slot].filter(Boolean).join('-');
  // Keep a hyphen typed straight after the shelf letter, so composites like
  // S-01-D-P13 still type by hand (the next key decides what it is).
  return !slot && /[-\s]$/.test(upper) ? `${formatted}-` : formatted;
}

/**
 * A bin code that names a shelf but no slot (T-05-A), after shorthand
 * expansion. These are readable but should not be saved from the entry
 * points that force a slot (ShelfSlotPicker).
 * @param {string} code
 * @returns {{ code: string, row: string, bay: number, shelf: string } | null}
 */
export function parseShelfOnlyBinCode(code) {
  const expanded = expandLocationShorthand(code);
  const match = expanded.match(SHELF_ONLY_BIN_REGEX);
  if (!match) return null;
  return { code: expanded, row: match[1], bay: parseInt(match[2], 10), shelf: match[3] };
}

export function isShelfOnlyBinCode(code) {
  return parseShelfOnlyBinCode(code) != null;
}

/** T-05-A + 4 → T-05-A-04. Returns null when shelfCode is not shelf-only. */
export function withShelfSlot(shelfCode, slot) {
  const shelf = parseShelfOnlyBinCode(shelfCode);
  const num = parseInt(slot, 10);
  if (!shelf || !Number.isFinite(num) || num < 1 || num > 99) return null;
  return `${shelf.code}-${String(num).padStart(2, '0')}`;
}

/** Staff sometimes enter shelf + pallet as one code, e.g. S-01-D-P13 → shelf S-01-D, overflow P13 */
export function splitBinPalletComposite(code) {
  const normalized = normalizeLocationCode(code);
  if (!normalized) return null;

  const match = normalized.match(/^([ZYXWVUTS]-\d{2}-[A-Z](?:-\d{2})?)-P-?(\d{1,3})$/);
  if (!match) return null;

  const palletNum = parseInt(match[2], 10);
  if (palletNum < 1 || palletNum > 200) return null;

  return {
    binLocation: match[1],
    overflowLocation: `P${palletNum}`
  };
}

export function parseWarehouseLocation(code) {
  const raw = String(code || '').trim();
  if (!raw) return null;

  const upper = normalizeLocationCode(raw);
  if (!upper) return null;

  const malformed = MALFORMED_LOCATION_REGEX.test(upper);
  const suggestedFix = malformed ? suggestLocationFix(upper) : null;

  let match = upper.match(/^P-?(\d{1,3})$/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (num >= 1 && num <= 200) {
      const normalized = `P${num}`;
      return {
        raw,
        normalized,
        canonicalKey: normalized,
        type: 'pallet',
        row: 'P',
        bay: num,
        side: null,
        slot: null,
        palletNumber: num,
        malformed: false
      };
    }
  }

  const multiSlot = parseMultiSlotBinSegment(upper);
  if (multiSlot) {
    const { row, bay, side, slots, normalized } = multiSlot;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot: slots[0],
      slots,
      multiSlot: true,
      palletNumber: null,
      malformed: normalized !== upper,
      suggestedFix: normalized !== upper ? normalized : null,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])-(\d{2})$/);
  if (match) {
    const row = match[1];
    return {
      raw,
      normalized: upper,
      canonicalKey: upper,
      type: 'bin',
      row,
      bay: parseInt(match[2], 10),
      side: match[3],
      slot: parseInt(match[4], 10),
      palletNumber: null,
      malformed,
      suggestedFix,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])-(\d)$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[3];
    const slot = parseInt(match[4], 10);
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${String(slot).padStart(2, '0')}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d)-([A-Z])-(\d{2})$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[3];
    const slot = parseInt(match[4], 10);
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${String(slot).padStart(2, '0')}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])(\d{1,2})$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[3];
    const slot = parseInt(match[4], 10);
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${String(slot).padStart(2, '0')}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-(\d+)([A-Z])-(\d{2})$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[4];
    const slot = parseInt(match[5], 10);
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${String(slot).padStart(2, '0')}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-(\d+)([A-Z])-(\d)$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[4];
    const slot = parseInt(match[5], 10);
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}-${String(slot).padStart(2, '0')}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-(\d+)([A-Z])$/);
  if (match) {
    const row = match[1];
    const bay = parseInt(match[2], 10);
    const side = match[4];
    const normalized = `${row}-${String(bay).padStart(2, '0')}-${side}`;
    return {
      raw,
      normalized,
      canonicalKey: normalized,
      type: 'bin',
      row,
      bay,
      side,
      slot: null,
      palletNumber: null,
      malformed: true,
      suggestedFix: normalized,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})-([A-Z])$/);
  if (match) {
    const row = match[1];
    return {
      raw,
      normalized: upper,
      canonicalKey: upper,
      type: 'bin',
      row,
      bay: parseInt(match[2], 10),
      side: match[3],
      slot: null,
      palletNumber: null,
      malformed,
      suggestedFix,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  match = upper.match(/^([ZYXWVUTS])-(\d{2})$/);
  if (match) {
    const row = match[1];
    return {
      raw,
      normalized: upper,
      canonicalKey: upper,
      type: 'bin',
      row,
      bay: parseInt(match[2], 10),
      side: null,
      slot: null,
      palletNumber: null,
      malformed,
      suggestedFix,
      unknownRow: !VALID_BIN_ROWS.includes(row)
    };
  }

  const floorCode = normalizeBayFloorLocationCode(upper);
  if (floorCode) {
    const floorMatch = floorCode.match(/^([XZ])-(\d{2})-FLOOR$/);
    const row = floorMatch[1];
    const bay = parseInt(floorMatch[2], 10);
    return {
      raw,
      normalized: floorCode,
      canonicalKey: floorCode,
      type: 'bay_floor',
      row,
      bay,
      side: null,
      slot: null,
      palletNumber: null,
      malformed: false,
      unknownRow: false
    };
  }

  match = upper.match(/^Z-(\d{2})-END-RACK$/);
  if (match) {
    const bay = parseInt(match[1], 10);
    return {
      raw,
      normalized: upper,
      canonicalKey: upper,
      type: 'end_rack',
      row: 'Z',
      bay,
      side: null,
      slot: null,
      palletNumber: null,
      malformed: false,
      unknownRow: false
    };
  }

  return {
    raw,
    normalized: upper,
    canonicalKey: upper,
    type: 'unknown',
    row: null,
    bay: null,
    side: null,
    slot: null,
    palletNumber: null,
    malformed,
    suggestedFix,
    unknownRow: true
  };
}

export function normalizeBayKey(bayKey) {
  if (!bayKey) return null;
  const upper = String(bayKey).trim().toUpperCase();
  const match = upper.match(/^([A-Z])-(\d{1,2})$/);
  if (match) {
    return `${match[1]}-${String(parseInt(match[2], 10)).padStart(2, '0')}`;
  }
  return upper;
}

/** Canonical X/Z rack floor code, e.g. Z-02 FLOOR → Z-02-FLOOR. */
export function normalizeBayFloorLocationCode(code) {
  const upper = normalizeLocationCode(code).replace(/[\s_]+/g, '-');
  if (!upper) return null;
  const match = upper.match(/^([XZ])-(\d{1,2})-FLOOR$/i);
  if (!match) return null;
  return `${match[1].toUpperCase()}-${String(parseInt(match[2], 10)).padStart(2, '0')}-FLOOR`;
}

export function getBayKey(parsed) {
  if (!parsed) return null;
  if (parsed.type === 'pallet') return `P-${parsed.palletNumber}`;
  if (parsed.type === 'end_rack') return parsed.canonicalKey || null;
  if ((parsed.type === 'bin' || parsed.type === 'bay_floor') && parsed.row && parsed.bay != null) {
    return `${parsed.row}-${String(parsed.bay).padStart(2, '0')}`;
  }
  const floorCode = normalizeBayFloorLocationCode(parsed.canonicalKey || parsed.normalized);
  if (floorCode) return floorCode.replace(/-FLOOR$/i, '');
  return parsed.canonicalKey || null;
}

/** Rack bay floor code staff enter instead of a pallet slot, e.g. Z-02-FLOOR or X-02-FLOOR. */
export function isBayFloorDisplayLocation(location) {
  return normalizeBayFloorLocationCode(location) != null;
}

/** Small end rack in Y–Z aisle north gap, e.g. Z-01-END-RACK. */
export function isEndRackLocation(location) {
  return /^Z-\d{2}-END-RACK$/i.test(String(location || '').trim());
}

/** Slot belongs to bayKey without pulling special locations (e.g. Z-01-END-RACK into Z-01). */
export function locationBelongsToBay(expandedLoc, parsed, bayKey) {
  const viewKey = normalizeBayKey(bayKey);
  const loc = String(expandedLoc || '').trim().toUpperCase();
  const slotBay = normalizeBayKey(getBayKey(parsed));
  if (loc === viewKey || slotBay === viewKey) return true;
  if (!loc.startsWith(`${viewKey}-`)) return false;
  if (parsed?.type === 'end_rack') return false;
  if (parsed?.type === 'bay_floor') return false;
  return parsed?.type === 'bin' || parsed?.type === 'unknown' || parsed?.malformed;
}

export function getSlotKey(parsed) {
  if (!parsed || parsed.type !== 'bin') return parsed?.canonicalKey || null;
  if (parsed.multiSlot) return parsed.canonicalKey;
  if (parsed.side && parsed.slot) {
    return `${parsed.row}-${String(parsed.bay).padStart(2, '0')}-${parsed.side}-${String(parsed.slot).padStart(2, '0')}`;
  }
  return parsed.canonicalKey;
}

/**
 * Staff enter one code for stock spanning multiple slot positions, e.g. U-03-J-05+08.
 * Returns individual slot codes or null when the code is not a multi-slot pattern.
 */
export function expandMultiSlotLocations(code) {
  const parsed = parseMultiSlotBinSegment(normalizeLocationCode(code));
  if (!parsed) return null;

  const prefix = `${parsed.row}-${String(parsed.bay).padStart(2, '0')}-${parsed.side}`;
  return parsed.slots.map((slot) => `${prefix}-${String(slot).padStart(2, '0')}`);
}

function flattenMultiSlotExpansions(entries) {
  const out = [];
  entries.forEach((entry) => {
    const multi = expandMultiSlotLocations(entry.location);
    if (multi?.length > 1) {
      multi.forEach((location) => {
        out.push({ location, composite: entry.composite, role: entry.role });
      });
      return;
    }
    out.push(entry);
  });
  return out;
}

/**
 * Expand composite locations staff enter with slashes, e.g. T-01-J/H or T-01-E/J-03.
 * Also expands multi-slot codes, e.g. U-03-J-05+08 → U-03-J-05 and U-03-J-08.
 */
export function expandLocationCodes(code) {
  const normalized = normalizeLocationCode(code);
  if (!normalized) return [];

  const binPallet = splitBinPalletComposite(normalized);
  if (binPallet) {
    return [
      { location: binPallet.binLocation, composite: normalized, role: 'primary' },
      { location: binPallet.overflowLocation, composite: normalized, role: 'overflow' }
    ];
  }

  const multiSlotLocations = expandMultiSlotLocations(normalized);
  if (multiSlotLocations?.length > 1) {
    return multiSlotLocations.map((location) => ({
      location,
      composite: normalized,
      role: 'primary'
    }));
  }

  if (!normalized.includes('/')) {
    return [{ location: normalized, composite: normalized, role: 'primary' }];
  }

  const segments = normalized.split('/').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return [];

  const firstParsed = parseWarehouseLocation(segments[0]);
  const bayPrefix = firstParsed?.row && firstParsed?.bay != null
    ? `${firstParsed.row}-${String(firstParsed.bay).padStart(2, '0')}`
    : null;
  const shelfPrefix = bayPrefix ? `${bayPrefix}-` : null;

  return flattenMultiSlotExpansions(segments.map((segment, index) => {
    let resolved = segment;
    if (index > 0 && shelfPrefix) {
      if (/^FLOOR$/i.test(segment) && bayPrefix) {
        resolved = `${bayPrefix}-FLOOR`;
      } else if (/^[A-Z]$/.test(segment)) {
        resolved = `${shelfPrefix}${segment}`;
      } else {
        const multiPartial = segment.match(/^([A-Z])-(\d{1,2}(?:\+\d{1,2})+)$/);
        if (multiPartial) {
          const slots = multiPartial[2].split('+').map((part) => String(parseInt(part, 10)).padStart(2, '0')).join('+');
          resolved = `${shelfPrefix}${multiPartial[1]}-${slots}`;
        } else {
          const partial = segment.match(/^([A-Z])-(\d{1,2})$/);
          if (partial) {
            resolved = `${shelfPrefix}${partial[1]}-${String(parseInt(partial[2], 10)).padStart(2, '0')}`;
          } else {
            const slotOnlyMulti = segment.match(/^(\d{1,2}(?:\+\d{1,2})+)$/);
            if (slotOnlyMulti && firstParsed?.side) {
              const slots = slotOnlyMulti[1].split('+').map((part) => String(parseInt(part, 10)).padStart(2, '0')).join('+');
              resolved = `${shelfPrefix}${firstParsed.side}-${slots}`;
            }
          }
        }
      }
    }
    return {
      location: resolved,
      composite: normalized,
      role: index === 0 ? 'primary' : 'overflow'
    };
  }));
}

/** Primary pick face for a location string (handles composites and slashes) */
export function resolvePrimaryLocation(code) {
  const expansions = expandLocationCodes(code);
  const primary = expansions.find(e => e.role === 'primary') || expansions[0];
  return primary?.location || normalizeLocationCode(code) || null;
}

/** Overflow pallet / secondary locations tied to a composite or slash code */
export function resolveOverflowLocations(code) {
  return expandLocationCodes(code)
    .filter(e => e.role === 'overflow')
    .map(e => e.location);
}

export { VALID_BIN_ROWS, MALFORMED_LOCATION_REGEX, MULTI_SLOT_BIN_REGEX };
